// Whether this device can reach the site, as one answer for the whole app
// (scenario M4, DIRECTION U16, B-558, PLAN-2026-10-29-game-day-with-no-signal
// §2 and §3).
//
// THE BROWSER'S WORD IS A FLOOR, NOT THE TRUTH. `navigator.onLine` is false
// only when there is no network at all; it is true on a stadium's Wi-Fi that
// drops every packet, on a café's sign-in page and while the site is down. So
// every request says how it went (`api.ts`), and a request that got no answer
// is as good a sign as the browser's.
//
//   online        a request was answered, and the browser has a network
//   offline       the browser has no network
//   reconnecting  the browser has just found one again, and nothing has
//                 answered yet
//   unreachable   the browser has a network and the last request got no
//                 answer: it threw, it timed out, or what came back was not
//                 this API
//   back          one of the three above has just ended: said for a moment
//
// A REFUSAL IS AN ANSWER, so it counts as reachable. A request its caller
// called off is no answer about the connection, and counts for nothing. Nor
// is a request sent before one that has since answered: on a connection that
// has just come back, the one sent into the dead minute still times out, and
// it says nothing about now.
//
// WHEN IT COMES BACK, every read on screen is asked again, so a screen catches
// up without a reload. While the site cannot be reached, they are asked again
// on a widening interval while the page is visible: that is how a connection
// that recovers without the browser noticing is found.
//
// No React and no browser in `createConnection`: the browser's word, the
// timer and the page's visibility are handed in, and `connection.test.ts`
// drives the rules as calls.

import { useSyncExternalStore } from 'react';

export type ConnectionState = 'online' | 'offline' | 'reconnecting' | 'unreachable' | 'back';

/** How long *Back online* stays up. */
export const BACK_MS = 3_000;

/** The waits between asking again while the site cannot be reached; the last repeats. */
export const RETRY_MS: readonly number[] = [5_000, 10_000, 20_000, 30_000];

export interface ConnectionOptions {
  /** Whether the browser says it has a network. */
  browserOnline: () => boolean;
  /** Whether the page is on screen: nothing is asked again while it is hidden. */
  visible: () => boolean;
  /** Run `fn` once `ms` have passed; returns the way to cancel it. */
  after: (ms: number, fn: () => void) => () => void;
  /** Ask again every read on screen; settles when they have all answered or failed. */
  refresh: () => Promise<void>;
}

export interface Connection {
  state(): ConnectionState;
  subscribe(listener: () => void): () => void;
  /** A request is leaving: its number, to hand back with how it went. */
  begin(): number;
  /** A request was answered, with anything at all from this API. */
  answered(ticket?: number): void;
  /** A request got no answer. */
  noAnswer(ticket?: number): void;
  /** The browser's online or offline event. */
  browserChanged(): void;
  /** The page came back on screen. */
  shown(): void;
  /** *Try again*, from the strip. */
  retry(): void;
  /** Whether a read should draw what the device kept at once rather than wait. */
  isDown(): boolean;
}

export function createConnection(options: ConnectionOptions): Connection {
  // What the requests have said. `offline` is never stored: it is the
  // browser's word, read every time.
  let down: 'reconnecting' | 'unreachable' | null = null;
  let wasOffline = !options.browserOnline();
  let back = false;
  let cancelBack: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  let tries = 0;
  /** The requests sent, and the newest of them that was answered. */
  let sent = 0;
  let newestAnswered = 0;
  /** Rounds of asking again still out. */
  let refreshing = 0;
  const listeners = new Set<() => void>();

  const state = (): ConnectionState => {
    if (!options.browserOnline()) return 'offline';
    if (down !== null) return down;
    return back ? 'back' : 'online';
  };
  // What listeners were last told, so they hear only a change.
  let last = state();

  const tell = () => {
    const now = state();
    if (now === last) return;
    last = now;
    for (const listener of [...listeners]) listener();
  };

  const stopRetrying = () => {
    cancelRetry?.();
    cancelRetry = null;
  };

  const stopBack = () => {
    cancelBack?.();
    cancelBack = null;
    back = false;
  };

  const sayBack = () => {
    stopBack();
    back = true;
    cancelBack = options.after(BACK_MS, () => {
      cancelBack = null;
      back = false;
      tell();
    });
  };

  const ask = () => {
    refreshing += 1;
    void options.refresh().then(() => {
      refreshing -= 1;
      // Nothing on screen asked anything, so nothing said the site is down:
      // the browser's word stands.
      if (down === 'reconnecting') {
        down = null;
        tell();
      }
    });
  };

  const scheduleRetry = () => {
    if (cancelRetry !== null) return;
    const wait = RETRY_MS[Math.min(tries, RETRY_MS.length - 1)]!;
    cancelRetry = options.after(wait, () => {
      cancelRetry = null;
      if (down !== 'unreachable' || !options.browserOnline()) return;
      // Hidden: asked when it is shown again (`shown`), not in the background.
      if (!options.visible()) return;
      tries += 1;
      ask();
      if (down === 'unreachable') scheduleRetry();
    });
  };

  return {
    state,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    begin() {
      sent += 1;
      return sent;
    },

    answered(ticket) {
      if (ticket !== undefined && ticket > newestAnswered) newestAnswered = ticket;
      wasOffline = false;
      if (down !== null) {
        down = null;
        tries = 0;
        stopRetrying();
        sayBack();
        // WHAT IS ON SCREEN CATCHES UP whatever answered first — a chat poll,
        // the bell, a screen just opened — so *Catching up now* is true: the
        // reads drawn from the device are asked again, unless a round of
        // asking is already out and they are in it.
        if (refreshing === 0) ask();
      }
      tell();
    },

    noAnswer(ticket) {
      // The browser has already said why.
      if (!options.browserOnline()) return;
      // Sent before a request that has since been answered: old news.
      if (ticket !== undefined && ticket < newestAnswered) return;
      stopBack();
      down = 'unreachable';
      scheduleRetry();
      tell();
    },

    browserChanged() {
      if (!options.browserOnline()) {
        wasOffline = true;
        stopRetrying();
        stopBack();
        tell();
        return;
      }
      if (wasOffline || down !== null) {
        wasOffline = false;
        down = 'reconnecting';
        stopRetrying();
        tries = 0;
        tell();
        ask();
      }
    },

    shown() {
      if (down !== 'unreachable' || !options.browserOnline()) return;
      stopRetrying();
      ask();
      scheduleRetry();
    },

    retry() {
      if (!options.browserOnline()) return;
      stopRetrying();
      tries = 0;
      ask();
      if (down === 'unreachable') scheduleRetry();
    },

    isDown() {
      const now = state();
      return now === 'offline' || now === 'unreachable' || now === 'reconnecting';
    },
  };
}

// ONE FOR THE PAGE. What asks again is handed in by whoever holds reads
// (`read.ts`), since this file is below them: `api.ts` reports to it.
const refreshers = new Set<() => Promise<void>>();

/** Be asked to read again when the connection comes back, or is tried. */
export function onReconnect(refresh: () => Promise<void>): () => void {
  refreshers.add(refresh);
  return () => refreshers.delete(refresh);
}

export const connection: Connection = createConnection({
  browserOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  visible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  after: (ms, fn) => {
    const timer = setTimeout(fn, ms);
    return () => clearTimeout(timer);
  },
  refresh: () => Promise.all([...refreshers].map((r) => r())).then(() => undefined),
});

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => connection.browserChanged());
  window.addEventListener('offline', () => connection.browserChanged());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') connection.shown();
  });
}

const subscribe = (changed: () => void) => connection.subscribe(changed);
const read = () => connection.state();
// A render with no window (a test, a server) is online: there is nothing to warn about.
const readOnServer = (): ConnectionState => 'online';

/** The connection's state, for the strip and for what a screen says about its age. */
export function useConnection(): ConnectionState {
  return useSyncExternalStore(subscribe, read, readOnServer);
}
